import { forwardRef } from 'react'

import { Progress as ProgressPrimitive } from '@base-ui/react/progress'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

const progressBarVariants = tv({
  slots: {
    root: 'flex w-full items-center gap-2',
    track: 'relative w-full overflow-hidden rounded-full',
    indicator: 'h-full rounded-full transition-all',
    value: 'text-surface-body shrink-0 text-xs tabular-nums'
  },
  variants: {
    size: {
      sm: { track: 'h-1.5' },
      md: { track: 'h-2' }
    },
    color: {
      primary: { indicator: 'bg-primary-200', track: 'bg-primary-50' },
      success: { indicator: 'bg-success-icon', track: 'bg-success-bg' },
      danger: { indicator: 'bg-danger-icon', track: 'bg-danger-bg' }
    }
  },
  defaultVariants: { size: 'md', color: 'primary' }
})

export type ProgressBarProps = React.ComponentPropsWithRef<
  typeof ProgressPrimitive.Root
> &
  VariantProps<typeof progressBarVariants> & {
    value: number
    max?: number
    showValue?: boolean
  }

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  ({ value, max = 100, size, color, showValue, className, ...props }, ref) => {
    const styles = progressBarVariants({ size, color })
    const clampedValue = Math.min(Math.max(value, 0), max)

    return (
      <ProgressPrimitive.Root
        ref={ref}
        data-slot='progress-bar'
        value={clampedValue}
        max={max}
        className={cn(styles.root(), className)}
        {...props}
      >
        <ProgressPrimitive.Track className={styles.track()}>
          <ProgressPrimitive.Indicator className={styles.indicator()} />
        </ProgressPrimitive.Track>
        {showValue ? (
          <ProgressPrimitive.Value className={styles.value()} />
        ) : null}
      </ProgressPrimitive.Root>
    )
  }
)
ProgressBar.displayName = 'ProgressBar'
